"use client";

import { useLayoutEffect, useRef } from "react";

import hljs from "highlight.js/lib/core";
import plaintext from "highlight.js/lib/languages/plaintext";
import markdown from "highlight.js/lib/languages/markdown";
import json from "highlight.js/lib/languages/json";
import javascript from "highlight.js/lib/languages/javascript";
import typescript from "highlight.js/lib/languages/typescript";
import xml from "highlight.js/lib/languages/xml";
import yaml from "highlight.js/lib/languages/yaml";
import cssLang from "highlight.js/lib/languages/css";
import less from "highlight.js/lib/languages/less";
import swift from "highlight.js/lib/languages/swift";
import objectivec from "highlight.js/lib/languages/objectivec";
import python from "highlight.js/lib/languages/python";

import php from "highlight.js/lib/languages/php";
import perl from "highlight.js/lib/languages/perl";
import java from "highlight.js/lib/languages/java";
import shell from "highlight.js/lib/languages/bash";

hljs.registerLanguage("plaintext", plaintext);
hljs.registerLanguage("markdown", markdown);
hljs.registerLanguage("json", json);
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("typescript", typescript);
hljs.registerLanguage("xml", xml);
hljs.registerLanguage("yaml", yaml);
hljs.registerLanguage("css", cssLang);
hljs.registerLanguage("less", less);
hljs.registerLanguage("swift", swift);
hljs.registerLanguage("objectivec", objectivec);
hljs.registerLanguage("python", python);
hljs.registerLanguage("php", php);
hljs.registerLanguage("perl", perl);
hljs.registerLanguage("java", java);
hljs.registerLanguage("shell", shell);

/**
 *
 * @param {String} textContent The text string to be highlighted, even if it already exists in the target div
 * @param {String} language The language to use to highlight with
 * @returns {Object} A ref that should be assigned to the HTML node that should contain the highlighted code
 *
 * Fills a div with the given code formatted according to the given language
 *
 * const codeRef = useCodeHighlighting('var stuff = { thing: "blargh" };', "javascript");
 *
 * <pre>
 *   <code ref={codeRef} />
 * </pre>
 */

export default function useCodeHighlighting(textContent: string, language: string) {
  const codeSnippetRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const currentNode = codeSnippetRef.current;
    if (currentNode && textContent) {
      if (!language || language === "Auto") {
        currentNode.innerHTML = hljs.highlightAuto(textContent).value;
      } else
        currentNode.innerHTML = hljs.highlight(textContent, {
          language: language.toLowerCase(),
        }).value;
    }
  }, [language, textContent]);

  return codeSnippetRef;
}
